import { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { de } from 'date-fns/locale';
import axios from 'axios';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales: { de },
});

const API = `${window.location.protocol}//${window.location.hostname}/api`;

const BOOKING_TYPES = [
  { value: 'guest', label: '👤 Gast' },
  { value: 'family', label: '👨‍👩‍👧 Familie' },
  { value: 'cleaning', label: '🧹 Reinigung' },
  { value: 'meeting', label: '📋 Meeting' },
];

const TYPE_COLORS = {
  guest: '#3174ad',
  family: '#24387F',
  cleaning: '#e67e22',
  meeting: '#27ae60',
};

const TYPE_LABELS = {
  guest: '👤 Gast',
  family: '👨‍👩‍👧 Familie',
  cleaning: '🧹 Reinigung',
  meeting: '📋 Meeting',
};

function formatDate(date) {
  return date.toISOString().split('T')[0];
}

// ── Styles ────────────────────────────────────────────────────────
const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontWeight: 'bold',
  fontSize: '13px',
  color: '#555'
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '8px',
  border: '1px solid #ddd',
  fontSize: '14px',
  boxSizing: 'border-box',
  fontFamily: 'Arial'
};

const btnPrimary = {
  padding: '10px 20px',
  backgroundColor: '#24387F',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 'bold'
};

const btnSecondary = {
  padding: '10px 20px',
  backgroundColor: '#eee',
  color: '#333',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '14px'
};

const btnDanger = {
  padding: '10px 20px',
  backgroundColor: '#e74c3c',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '14px'
};

// ── Login Screen ──────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleLogin() {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/login`, { email, password });
      onLogin(res.data.token, res.data.user);
    } catch (err) {
      setError('Email oder Passwort falsch');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundImage: 'url(https://m6ns4myltpm3eca-zqu8slep6bkizht8.adb.eu-frankfurt-1.oraclecloudapps.com/ords/r/apex_jschlott/100/files/static/v6/SyltMeer.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: '#cccccc',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Arial'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '48px',
        width: '400px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img src="/leuchtturm.jpg" alt="Strand22" style={{ height: '80px', marginBottom: '8px' }} />
          <h1 style={{ margin: 0, color: '#24387F', fontSize: '24px' }}>
            Strand22
          </h1>
          <p style={{ margin: '8px 0 0', color: '#888', fontSize: '14px' }}>
            Bitte melde dich an
          </p>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fdecea',
            color: '#e74c3c',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            ⚠️ {error}
          </div>
        )}

        <form onSubmit={e => { e.preventDefault(); handleLogin(); }}>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="deine@email.com"
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={labelStyle}>Passwort</label>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            style={inputStyle}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            ...btnPrimary,
            width: '100%',
            padding: '14px',
            fontSize: '16px',
            opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? 'Anmelden...' : 'Anmelden'}
        </button>
        </form>
      </div>
    </div>
  );
}

// ── Buchungs-Modal ────────────────────────────────────────────────
function Modal({ booking, onSave, onDelete, onClose }) {
  const [form, setForm] = useState({
    guest_name: booking?.guest_name || '',
    arrival_date: booking?.arrival_date?.split('T')[0] || formatDate(booking?.start || new Date()),
    departure_date: booking?.departure_date?.split('T')[0] || formatDate(booking?.end || new Date()),
    booking_type: booking?.booking_type || 'guest',
    notes: booking?.notes || '',
  });
  const [saveError, setSaveError] = useState('');
  const isNew = !booking?.id;

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSave() {
    setSaveError('');
    if (!form.guest_name.trim()) { setSaveError('Bitte einen Gastnamen eingeben.'); return; }
    if (form.departure_date < form.arrival_date) { setSaveError('Abfahrt darf nicht vor der Ankunft liegen.'); return; }
    const today = new Date().toISOString().split('T')[0];
    if (form.departure_date < today) {
      if (!window.confirm('Die Abfahrt liegt in der Vergangenheit. Trotzdem speichern?')) return;
    }
    try {
      await onSave({ ...form, id: booking?.id });
    } catch (err) {
      setSaveError(err.message);
    }
  }

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '32px',
        width: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <h2 style={{ margin: '0 0 24px', color: '#24387F' }}>
          {isNew ? '➕ Neue Belegung' : '✏️ Belegung bearbeiten'}
        </h2>

        {saveError && (
          <div style={{
            backgroundColor: '#fdecea', color: '#e74c3c', padding: '10px 12px',
            borderRadius: '8px', marginBottom: '16px', fontSize: '13px'
          }}>⚠️ {saveError}</div>
        )}

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Gast / Bezeichnung</label>
          <input name="guest_name" value={form.guest_name} onChange={handleChange}
            placeholder="z.B. Jan und Andrea" style={inputStyle} />
        </div>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Ankunft</label>
            <input type="date" name="arrival_date" value={form.arrival_date} onChange={handleChange} style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Abfahrt</label>
            <input type="date" name="departure_date" value={form.departure_date} onChange={handleChange} style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Typ</label>
          <select name="booking_type" value={form.booking_type} onChange={handleChange} style={inputStyle}>
            {BOOKING_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Notizen</label>
          <textarea name="notes" value={form.notes} onChange={handleChange}
            placeholder="Optionale Notizen..." rows={3}
            style={{ ...inputStyle, resize: 'vertical' }} />
        </div>

        {!isNew && (
          <div style={{
            marginBottom: '24px', padding: '12px', backgroundColor: '#f8f9fa',
            borderRadius: '8px', fontSize: '12px', color: '#666'
          }}>
            <div style={{ marginBottom: '6px' }}>
              📝 <strong>Angelegt von:</strong> {booking.created_by_name || '—'}
              {booking.created_at && ` am ${new Date(booking.created_at).toLocaleDateString('de-DE')}`}
            </div>
            <div>
              ✏️ <strong>Zuletzt geändert von:</strong> {booking.updated_by_name || '—'}
              {booking.updated_at && ` am ${new Date(booking.updated_at).toLocaleDateString('de-DE')}`}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleSave} style={btnPrimary}>💾 Speichern</button>
            <button onClick={onClose} style={btnSecondary}>Abbrechen</button>
          </div>
          {!isNew && (
            <button onClick={() => onDelete(booking.id)} style={btnDanger}>🗑️ Löschen</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Kalender Toolbar ───────────────────────────────────────────────
function CalendarToolbar({ label, onNavigate }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
      <button onClick={() => onNavigate('TODAY')} style={btnSecondary}>Heute</button>
      <button onClick={() => onNavigate('PREV')} style={btnSecondary}>‹</button>
      <button onClick={() => onNavigate('NEXT')} style={btnSecondary}>›</button>
      <span style={{ fontWeight: 'bold', fontSize: '16px', marginLeft: '8px', color: '#24387F' }}>
        {label}
      </span>
    </div>
  );
}

// ── Belegungsliste ────────────────────────────────────────────────
function BookingList({ bookings, onEdit }) {
  const [sortCol, setSortCol] = useState('arrival_date');
  const [sortDir, setSortDir] = useState('desc');

  function toggleSort(col) {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  }

  const sorted = [...bookings].sort((a, b) => {
    let va = a[sortCol] || '';
    let vb = b[sortCol] || '';
    if (sortCol === 'arrival_date' || sortCol === 'departure_date') {
      va = new Date(va);
      vb = new Date(vb);
    } else {
      va = va.toString().toLowerCase();
      vb = vb.toString().toLowerCase();
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  function exportCSV() {
    const headers = ['Gast/Bezeichnung', 'Ankunft', 'Abfahrt', 'Typ', 'Notizen'];
    const rows = sorted.map(b => [
      b.guest_name || 'Belegt',
      new Date(b.arrival_date).toLocaleDateString('de-DE'),
      new Date(b.departure_date).toLocaleDateString('de-DE'),
      TYPE_LABELS[b.booking_type] || b.booking_type,
      b.notes || ''
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'belegungen.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function SortTh({ col, children }) {
    const active = sortCol === col;
    return (
      <th
        onClick={() => toggleSort(col)}
        style={{
          textAlign: 'left', padding: '10px 12px', color: active ? '#24387F' : '#555',
          cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap'
        }}
      >
        {children}
        <span style={{ marginLeft: '4px', opacity: active ? 1 : 0.3 }}>
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '▲'}
        </span>
      </th>
    );
  }

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <SortTh col="guest_name">Gast / Bezeichnung</SortTh>
              <SortTh col="arrival_date">Ankunft</SortTh>
              <SortTh col="departure_date">Abfahrt</SortTh>
              <SortTh col="booking_type">Typ</SortTh>
              <th style={{ textAlign: 'left', padding: '10px 12px', color: '#555' }}>Notizen</th>
              <th style={{ padding: '10px 12px' }}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(b => (
              <tr key={b.id} style={{ borderBottom: '1px solid #f0f0f0' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                <td style={{ padding: '10px 12px', fontWeight: '500' }}>{b.guest_name || 'Belegt'}</td>
                <td style={{ padding: '10px 12px', color: '#555' }}>
                  {new Date(b.arrival_date).toLocaleDateString('de-DE')}
                </td>
                <td style={{ padding: '10px 12px', color: '#555' }}>
                  {new Date(b.departure_date).toLocaleDateString('de-DE')}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{
                    padding: '2px 10px', borderRadius: '12px', fontSize: '12px',
                    backgroundColor: (TYPE_COLORS[b.booking_type] || '#888') + '22',
                    color: TYPE_COLORS[b.booking_type] || '#888', fontWeight: 'bold'
                  }}>
                    {TYPE_LABELS[b.booking_type] || b.booking_type}
                  </span>
                </td>
                <td style={{ padding: '10px 12px', color: '#777', fontSize: '13px', maxWidth: '200px' }}>
                  {b.notes || '—'}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                  {onEdit && (
                    <button onClick={() => onEdit(b)}
                      style={{ ...btnSecondary, padding: '4px 12px', fontSize: '12px' }}>
                      ✏️ Bearbeiten
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '32px', textAlign: 'center', color: '#888' }}>
                  Keine Belegungen vorhanden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: '16px', textAlign: 'right' }}>
        <button onClick={exportCSV} style={{ ...btnSecondary, fontSize: '13px', padding: '8px 16px' }}>
          ⬇️ Als CSV exportieren
        </button>
      </div>
    </div>
  );
}

// ── Benutzer Modal ────────────────────────────────────────────────
const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'reader', label: 'Reader' },
];

function UserModal({ user, onSave, onClose }) {
  const isNew = !user?.id;
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'editor',
    password: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  function handleChange(e) { setForm({ ...form, [e.target.name]: e.target.value }); }

  async function handleSave() {
    if (!form.name.trim() || !form.email.trim()) { setError('Name und Email sind Pflichtfelder'); return; }
    if (isNew && !form.password.trim()) { setError('Passwort ist bei neuen Benutzern Pflicht'); return; }
    setSaving(true); setError('');
    try {
      await onSave({ ...form, id: user?.id });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '32px',
        width: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <h2 style={{ margin: '0 0 24px', color: '#24387F' }}>
          {isNew ? '➕ Neuer Benutzer' : '✏️ Benutzer bearbeiten'}
        </h2>
        {error && (
          <div style={{
            backgroundColor: '#fdecea', color: '#e74c3c', padding: '10px 12px',
            borderRadius: '8px', marginBottom: '16px', fontSize: '13px'
          }}>⚠️ {error}</div>
        )}
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>Name</label>
          <input name="name" value={form.name} onChange={handleChange} placeholder="Vor- und Nachname" style={inputStyle} />
        </div>
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>Email</label>
          <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="email@example.com" style={inputStyle} />
        </div>
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>Rolle</label>
          <select name="role" value={form.role} onChange={handleChange} style={inputStyle}>
            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: '24px' }}>
          <label style={labelStyle}>
            Passwort {!isNew && <span style={{ fontWeight: 'normal', color: '#888' }}>(leer lassen = nicht ändern)</span>}
          </label>
          <input name="password" type="password" value={form.password} onChange={handleChange}
            placeholder={isNew ? 'Passwort eingeben' : 'Neues Passwort (optional)'} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSecondary}>Abbrechen</button>
          <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Speichern...' : '💾 Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Benutzerverwaltung (Seite) ────────────────────────────────────
function UserManagement({ token, currentUserId }) {
  const [users, setUsers] = useState([]);
  const [userModal, setUserModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => { loadUsers(); }, []);

  function loadUsers() {
    setLoading(true);
    axios.get(`${API}/users`, authHeaders())
      .then(res => setUsers(res.data))
      .finally(() => setLoading(false));
  }

  async function handleSave(form) {
    if (form.id) {
      await axios.put(`${API}/users/${form.id}`, form, authHeaders());
    } else {
      await axios.post(`${API}/users`, form, authHeaders());
    }
    loadUsers();
    setUserModal(null);
  }

  async function handleDelete(user) {
    if (!window.confirm(`Benutzer "${user.name}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/users/${user.id}`, authHeaders());
      loadUsers();
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.error || err.message));
    }
  }

  const roleColors = { admin: '#24387F', editor: '#27ae60', reader: '#888' };
  const roleLabels = { admin: 'Admin', editor: 'Editor', reader: 'Reader' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: '#24387F' }}>👥 Benutzerverwaltung</h2>
        <button onClick={() => setUserModal({})} style={btnPrimary}>➕ Neuer Benutzer</button>
      </div>

      {loading ? (
        <p style={{ color: '#888' }}>Lade Benutzer...</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', color: '#555' }}>Name</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', color: '#555' }}>Email</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', color: '#555' }}>Rolle</th>
              <th style={{ padding: '10px 12px' }}></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '10px 12px', fontWeight: u.id === currentUserId ? 'bold' : 'normal' }}>
                  {u.name} {u.id === currentUserId && <span style={{ fontSize: '11px', color: '#888' }}>(du)</span>}
                </td>
                <td style={{ padding: '10px 12px', color: '#555' }}>{u.email}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{
                    padding: '2px 10px', borderRadius: '12px', fontSize: '12px',
                    backgroundColor: roleColors[u.role] + '22',
                    color: roleColors[u.role], fontWeight: 'bold'
                  }}>
                    {roleLabels[u.role]}
                  </span>
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                  <button onClick={() => setUserModal(u)}
                    style={{ ...btnSecondary, padding: '4px 12px', fontSize: '12px', marginRight: '6px' }}>
                    ✏️ Bearbeiten
                  </button>
                  {u.id !== currentUserId && (
                    <button onClick={() => handleDelete(u)}
                      style={{ ...btnDanger, padding: '4px 12px', fontSize: '12px' }}>
                      🗑️
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {userModal !== null && (
        <UserModal user={userModal} onSave={handleSave} onClose={() => setUserModal(null)} />
      )}
    </div>
  );
}

// ── Galerie Ansicht ──────────────────────────────────────────────
function GalleryView() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/gallery`).then(res => setItems(res.data)).catch(() => {});
  }, []);

  return (
    <div>
      <h2 style={{ margin: '0 0 24px', color: '#24387F', fontSize: '22px' }}>Moin Moin auf Sylt!</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
        {items.map(item => (
          <div key={item.id} style={{
            border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden',
            backgroundColor: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
          }}>
            <img src={item.image_url} alt={item.caption}
              style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }} />
            {item.caption && (
              <p style={{ margin: 0, padding: '14px 16px', fontSize: '14px', color: '#444', lineHeight: '1.5' }}>
                {item.caption}
              </p>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <p style={{ color: '#888', gridColumn: '1/-1' }}>Noch keine Bilder vorhanden.</p>
        )}
      </div>
    </div>
  );
}

// ── Galerie Verwaltung (Admin) ────────────────────────────────────

function ReportManagement({ token }) {
  const [recipients, setRecipients] = useState([]);
  const [form, setForm] = useState({ name: '', email: '' });
  const [msg, setMsg] = useState(null);
  const [sending, setSending] = useState(false);
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const load = () => {
    axios.get(`${API}/report/recipients`, authHeaders())
      .then(res => setRecipients(res.data))
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/report/recipients`, form, authHeaders());
      setForm({ name: '', email: '' });
      setMsg({ type: 'success', text: 'Empfänger hinzugefügt' });
      load();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Fehler' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Empfänger entfernen?')) return;
    await axios.delete(`${API}/report/recipients/${id}`, authHeaders());
    load();
  };

  const handleSend = async () => {
    setSending(true);
    setMsg(null);
    try {
      await axios.post(`${API}/report/send`, {}, authHeaders());
      setMsg({ type: 'success', text: 'Bericht wurde verschickt!' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Fehler beim Versand' });
    }
    setSending(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0, color: '#24387F' }}>Wochenbericht</h2>
        <button
          onClick={handleSend}
          disabled={sending}
          style={{ background: '#24387F', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.7 : 1 }}
        >
          {sending ? 'Wird gesendet...' : 'Jetzt senden'}
        </button>
      </div>

      <p style={{ color: '#666', marginBottom: '24px' }}>
        Jeden Montag um 07:00 Uhr wird automatisch ein Bericht mit allen Belegungen und den Änderungen der letzten 4 Wochen verschickt.
      </p>

      {msg && (
        <div style={{ padding: '12px', borderRadius: '8px', marginBottom: '16px', background: msg.type === 'success' ? '#d4edda' : '#f8d7da', color: msg.type === 'success' ? '#155724' : '#721c24' }}>
          {msg.text}
        </div>
      )}

      <h3 style={{ color: '#24387F', marginBottom: '16px' }}>Empfänger</h3>

      <form onSubmit={handleAdd} style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <input
          placeholder="Name"
          value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
          required
          style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', flex: '1', minWidth: '150px' }}
        />
        <input
          placeholder="Email"
          type="email"
          value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })}
          required
          style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', flex: '2', minWidth: '200px' }}
        />
        <button type="submit" style={{ background: '#24387F', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer' }}>
          Hinzufügen
        </button>
      </form>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f8f9fa' }}>
            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Name</th>
            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Email</th>
            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Hinzugefügt</th>
            <th style={{ padding: '10px', borderBottom: '2px solid #dee2e6' }}></th>
          </tr>
        </thead>
        <tbody>
          {recipients.map(r => (
            <tr key={r.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '10px' }}>{r.name}</td>
              <td style={{ padding: '10px' }}>{r.email}</td>
              <td style={{ padding: '10px', color: '#666' }}>{new Date(r.created_at).toLocaleDateString('de-DE')}</td>
              <td style={{ padding: '10px', textAlign: 'right' }}>
                <button onClick={() => handleDelete(r.id)} style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer' }}>
                  Entfernen
                </button>
              </td>
            </tr>
          ))}
          {recipients.length === 0 && (
            <tr><td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Keine Empfänger konfiguriert</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function GalleryManagement({ token }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ image_url: '', caption: '', sort_order: 0 });
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => { loadItems(); }, []);

  function loadItems() {
    axios.get(`${API}/gallery`).then(res => setItems(res.data)).catch(() => {});
  }

  function startEdit(item) {
    setEditing(item.id);
    setForm({ image_url: item.image_url, caption: item.caption, sort_order: item.sort_order });
    setError('');
  }

  function cancelEdit() {
    setEditing(null);
    setForm({ image_url: '', caption: '', sort_order: 0 });
    setError('');
  }

  async function handleSave() {
    if (!form.image_url.trim()) { setError('Bild-URL ist Pflicht'); return; }
    setError('');
    try {
      if (editing === 'new') {
        await axios.post(`${API}/gallery`, form, authHeaders());
      } else {
        await axios.put(`${API}/gallery/${editing}`, form, authHeaders());
      }
      loadItems();
      cancelEdit();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Bild wirklich loeschen?')) return;
    await axios.delete(`${API}/gallery/${id}`, authHeaders());
    loadItems();
  }

  const isFormOpen = editing !== null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: '#24387F' }}>Galerie verwalten</h2>
        {!isFormOpen && (
          <button onClick={() => { setEditing('new'); setForm({ image_url: '', caption: '', sort_order: items.length }); }}
            style={btnPrimary}>+ Bild hinzufuegen</button>
        )}
      </div>

      {isFormOpen && (
        <div style={{
          backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '20px',
          marginBottom: '24px', border: '1px solid #e0e0e0'
        }}>
          <h3 style={{ margin: '0 0 16px', color: '#24387F', fontSize: '15px' }}>
            {editing === 'new' ? 'Neues Bild' : 'Bild bearbeiten'}
          </h3>
          {error && (
            <div style={{ backgroundColor: '#fdecea', color: '#e74c3c', padding: '10px 12px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>
              {error}
            </div>
          )}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Bild-URL</label>
            <input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })}
              placeholder="https://..." style={inputStyle} />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Bildtext / Beschreibung</label>
            <textarea value={form.caption} onChange={e => setForm({ ...form, caption: e.target.value })}
              placeholder="Beschreibung des Bildes..." rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Reihenfolge</label>
            <input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
              style={{ ...inputStyle, width: '100px' }} />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleSave} style={btnPrimary}>Speichern</button>
            <button onClick={cancelEdit} style={btnSecondary}>Abbrechen</button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
        {items.map(item => (
          <div key={item.id} style={{
            border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#fff'
          }}>
            <img src={item.image_url} alt={item.caption}
              style={{ width: '100%', height: '160px', objectFit: 'cover', display: 'block' }} />
            <div style={{ padding: '12px' }}>
              <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#555', lineHeight: '1.4' }}>
                {item.caption || '(kein Text)'}
              </p>
              <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '10px' }}>
                Reihenfolge: {item.sort_order}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => startEdit(item)}
                  style={{ ...btnSecondary, padding: '4px 10px', fontSize: '12px' }}>Bearbeiten</button>
                <button onClick={() => handleDelete(item.id)}
                  style={{ ...btnDanger, padding: '4px 10px', fontSize: '12px' }}>Loeschen</button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && !isFormOpen && (
          <p style={{ color: '#888' }}>Noch keine Bilder vorhanden.</p>
        )}
      </div>
    </div>
  );
}

// ── Haupt App ─────────────────────────────────────────────────────
function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || 'null'));
  const [bookings, setBookings] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [modal, setModal] = useState(null);
  const [currentView, setCurrentView] = useState('home');
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  const isReader = user?.role === 'reader';
  const isAdmin = user?.role === 'admin';
  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  useEffect(() => {
    if (token) loadBookings();
  }, [token]);

  function handleLogin(newToken, newUser) {
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  }

  function handleLogout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  }

  function authHeaders() {
    return { headers: { Authorization: `Bearer ${token}` } };
  }

  function loadBookings() {
    axios.get(`${API}/bookings`, authHeaders()).then(res => {
      const events = res.data.map(b => ({
        ...b,
        title: b.guest_name,
        start: new Date(b.arrival_date),
        end: new Date(b.departure_date),
      }));
      setBookings(events);
    }).catch(() => handleLogout());
  }

  function handleSelectSlot({ start, end }) {
    if (isReader) return;
    setModal({ start, end });
  }

  function handleSelectEvent(event) {
    if (isReader) return;
    setModal(event);
  }

  async function handleSave(form) {
    try {
      if (form.id) {
        await axios.put(`${API}/bookings/${form.id}`, form, authHeaders());
      } else {
        await axios.post(`${API}/bookings`, form, authHeaders());
      }
      loadBookings();
      setModal(null);
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Belegung wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/bookings/${id}`, authHeaders());
      loadBookings();
      setModal(null);
    } catch (err) {
      alert('Fehler beim Löschen: ' + err.message);
    }
  }

  function eventStyleGetter(event) {
    const color = TYPE_COLORS[event.booking_type] || '#3174ad';
    return {
      style: {
        backgroundColor: color,
        borderRadius: '6px',
        border: 'none',
        color: 'white',
        padding: '2px 6px',
        fontSize: '12px',
      }
    };
  }

  if (!token) return <LoginScreen onLogin={handleLogin} />;

  const navItems = [
    { key: 'home', label: 'Home', icon: '🏠' },
    { key: 'calendar', label: 'Kalender', icon: '📅' },
    ...(canEdit ? [{ key: 'bookings', label: 'Belegungen', icon: '📋' }] : []),
    ...(isAdmin ? [{ key: 'gallery', label: 'Galerie', icon: '🖼️' }] : []),
    ...(isAdmin ? [{ key: 'users', label: 'Benutzerverwaltung', icon: '👥' }] : []),
    ...(isAdmin ? [{ key: 'reports', label: 'Berichte', icon: '📧' }] : []),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: 'Arial' }}>

      {/* ── Header ── */}
      <div style={{
        backgroundColor: '#24387F',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        height: '56px',
        flexShrink: 0,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
      }}>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{
            background: 'none', border: 'none', color: 'white',
            fontSize: '20px', cursor: 'pointer', padding: '8px', marginRight: '8px'
          }}
        >☰</button>

        {window.innerWidth > 480 && (
          <img src="/leuchtturm.jpg" alt="Strand22"
            style={{ height: '36px', marginRight: '12px', borderRadius: '4px' }} />
        )}

        <span style={{ fontSize: window.innerWidth <= 480 ? '16px' : '20px', fontWeight: 'bold', flex: 1 }}>Strand22</span>

        {window.innerWidth > 480 && (
          <span style={{ fontSize: '13px', marginRight: '16px', opacity: 0.85 }}>
            👤 <strong>{user?.name}</strong>
            <span style={{
              marginLeft: '8px', padding: '2px 8px', borderRadius: '12px',
              fontSize: '11px', backgroundColor: 'rgba(255,255,255,0.2)'
            }}>
              {user?.role}
            </span>
          </span>
        )}

        <button onClick={handleLogout} title="Abmelden" style={{
          background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
          color: 'white', borderRadius: '8px', cursor: 'pointer',
          padding: window.innerWidth <= 480 ? '6px 10px' : '6px 14px',
          fontSize: window.innerWidth <= 480 ? '16px' : '13px'
        }}>
          {window.innerWidth <= 480 ? '⏻' : 'Abmelden'}
        </button>
      </div>

      {/* ── Body ── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── Sidebar ── */}
        {sidebarOpen && window.innerWidth <= 768 && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 99
            }}
          />
        )}
        {sidebarOpen && (
          <div style={{
            width: '220px',
            backgroundColor: '#2d3748',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            paddingTop: '16px',
            ...(window.innerWidth <= 768 ? {
              position: 'fixed', top: '56px', left: 0, bottom: 0, zIndex: 100
            } : {})
          }}>
            {navItems.map(item => (
              <button
                key={item.key}
                onClick={() => { setCurrentView(item.key); if (window.innerWidth <= 768) setSidebarOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 20px',
                  background: currentView === item.key ? 'rgba(255,255,255,0.15)' : 'none',
                  border: 'none',
                  borderLeft: currentView === item.key ? '3px solid white' : '3px solid transparent',
                  color: 'white',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* ── Main Content ── */}
        <div style={{ flex: 1, padding: '24px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>

          {currentView === 'gallery' && isAdmin && (
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <GalleryManagement token={token} />
            </div>
          )}

          {currentView === 'home' && (
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <GalleryView />
            </div>
          )}

          {currentView === 'calendar' && (
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              {isReader && (
                <div style={{
                  backgroundColor: '#fff3cd', padding: '10px 16px', borderRadius: '8px',
                  marginBottom: '16px', fontSize: '13px', color: '#856404'
                }}>
                  Du siehst nur wann die Wohnung belegt ist - keine weiteren Details.
                </div>
              )}
              <style>{`
                .rbc-month-row { min-height: 60px !important; max-height: 60px !important; overflow: hidden; }
                .rbc-row-content { overflow: hidden; }
              `}</style>
              <Calendar
                localizer={localizer}
                events={bookings}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 500 }}
                culture="de"
                date={currentDate}
                onNavigate={date => setCurrentDate(date)}
                selectable={!isReader}
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                eventPropGetter={eventStyleGetter}
                views={['month']}
                defaultView="month"
                components={{ toolbar: CalendarToolbar }}
              />
            </div>
          )}

          {currentView === 'bookings' && canEdit && (
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <button onClick={() => setModal({ start: new Date(), end: new Date() })} style={btnPrimary}>
                  ➕ Neue Belegung
                </button>
              </div>
              <BookingList bookings={bookings} onEdit={b => setModal(b)} />
            </div>
          )}

          {currentView === 'users' && isAdmin && (
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <UserManagement token={token} currentUserId={user?.id} />
            </div>
          )}

          {currentView === 'reports' && isAdmin && (
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <ReportManagement token={token} />
            </div>
          )}
        </div>
      </div>

      {modal && (
        <Modal
          booking={modal}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

export default App;
